import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';

const Section = ({ title, children }) => (
  <section className="mb-5">
    <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '19px' }}>
      {title}
    </h5>
    {children}
  </section>
);

const CookieRow = ({ name, provider, type, purpose, duration, canOptOut }) => (
  <div className="rounded-3 p-3 mb-3" style={{ background: 'var(--header-bg)', border: '1px solid rgba(111,66,193,0.12)' }}>
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
      <code style={{ color: '#6f42c1', fontSize: '13px', fontWeight: 700, background: 'rgba(111,66,193,0.08)', padding: '2px 8px', borderRadius: '6px' }}>{name}</code>
      <div className="d-flex gap-2">
        <span className="badge rounded-pill" style={{
          background: type === 'Strictly Necessary' ? '#d1fae5' : type === 'Analytics' ? '#fef9c3' : '#e0e7ff',
          color: type === 'Strictly Necessary' ? '#065f46' : type === 'Analytics' ? '#92400e' : '#3730a3',
          fontSize: '11px',
        }}>
          {type}
        </span>
        {canOptOut ? (
          <span className="badge rounded-pill" style={{ background: '#fef9c3', color: '#92400e', fontSize: '11px' }}>Opt-out available</span>
        ) : (
          <span className="badge rounded-pill" style={{ background: 'var(--header-bg)', color: 'var(--text-muted2)', fontSize: '11px' }}>Required</span>
        )}
      </div>
    </div>
    <div className="row g-2" style={{ fontSize: '13px' }}>
      <div className="col-sm-3"><span className="text-muted">Provider:</span> <strong style={{ color: 'var(--text-body)' }}>{provider}</strong></div>
      <div className="col-sm-3"><span className="text-muted">Duration:</span> <strong style={{ color: 'var(--text-body)' }}>{duration}</strong></div>
      <div className="col-12"><span className="text-muted">Purpose:</span> <span style={{ color: 'var(--text-body)' }}>{purpose}</span></div>
    </div>
  </div>
);

const CookiePolicyPage = () => {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="Cookie Policy"
        description="Learn about the cookies and local storage Dreamy Bedtime uses, why we use them, and how to manage your preferences."
        canonical="/cookies"
        noIndex={false}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-dream text-white" style={{ padding: '56px 0 40px' }}>
        <div className="container text-center" style={{ maxWidth: '600px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">🍪 Cookies</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,44px)' }}>
            Cookie Policy
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '16px' }}>
            Last updated: May 25, 2026
          </p>
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '800px' }}>
        <div className="card border-0 shadow-sm p-4 p-lg-5" style={{ borderRadius: '24px' }}>

          <p className="text-muted mb-5" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            This Cookie Policy explains what cookies and similar storage technologies Dreamy Bedtime uses, why we use them, and how you can manage your preferences. This policy applies to the website at <strong>dreamybedtime.com</strong> and the Dreamy Bedtime Progressive Web App (PWA).
          </p>

          <Section title="1. What Are Cookies?">
            <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              Cookies are small text files placed in your browser by a website when you visit it. They allow the site to remember information about your visit — such as whether you're logged in or whether you've accepted cookies — so you don't have to repeat actions on every page or every visit.
            </p>
            <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              We also use <strong>local storage</strong> and <strong>session storage</strong> — browser technologies that work similarly to cookies but are stored differently. This policy covers all three.
            </p>
          </Section>

          <Section title="2. What We Use and Why">
            <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              We use three categories of storage: strictly necessary (required for the site to work), analytics (help us understand how people use the site — only activated with your consent), and functional (support specific features like affiliate tracking).
            </p>

            {/* Strictly Necessary */}
            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
              Strictly Necessary — No consent required
            </h6>
            <p className="text-muted mb-3" style={{ fontSize: '14px', lineHeight: 1.7 }}>
              These are stored in your browser's <strong>local storage</strong> or <strong>session storage</strong> (not traditional cookies). They are essential for the Service to function and cannot be disabled without breaking your experience.
            </p>

            <CookieRow
              name="authToken"
              provider="Dreamy Bedtime"
              type="Strictly Necessary"
              purpose="Stores your JSON Web Token (JWT) to keep you logged in across browser sessions. Without this you would need to log in on every page visit."
              duration="Until you log out or the token expires (~7 days)"
              canOptOut={false}
            />
            <CookieRow
              name="cookieConsent"
              provider="Dreamy Bedtime"
              type="Strictly Necessary"
              purpose="Stores your accept/decline decision for analytics cookies so we don't show you the cookie banner on every visit."
              duration="Permanent (until you clear local storage)"
              canOptOut={false}
            />
            <CookieRow
              name="pricingCache / geoCache"
              provider="Dreamy Bedtime"
              type="Strictly Necessary"
              purpose="Caches pricing data and your detected country/currency for the current browser tab to avoid repeating network requests on every page."
              duration="Session only (cleared when you close the tab)"
              canOptOut={false}
            />
            <CookieRow
              name="maintenanceMode / featureFlags"
              provider="Dreamy Bedtime"
              type="Strictly Necessary"
              purpose="Caches system settings so the app can display the correct features and maintenance status without a new API call on every page."
              duration="Session only"
              canOptOut={false}
            />

            {/* Analytics */}
            <h6 className="fw-bold mb-3 mt-4" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
              Analytics — Requires your consent
            </h6>
            <p className="text-muted mb-3" style={{ fontSize: '14px', lineHeight: 1.7 }}>
              These cookies are set by Google Analytics 4 and are only placed in your browser after you click <em>Accept</em> on our cookie banner. They help us understand how visitors use our site so we can improve it.
            </p>

            <CookieRow
              name="_ga"
              provider="Google Analytics 4"
              type="Analytics"
              purpose="The primary Google Analytics cookie. Distinguishes unique users by assigning a randomly generated number as a client identifier. Used to calculate visits, sessions, and traffic data in our analytics reports."
              duration="2 years"
              canOptOut={true}
            />
            <CookieRow
              name="_ga_[ID]"
              provider="Google Analytics 4"
              type="Analytics"
              purpose="Used to persist session state — stores information about the current session such as whether it is the user's first visit. The [ID] portion is specific to the GA4 property."
              duration="2 years"
              canOptOut={true}
            />
            <CookieRow
              name="_gid"
              provider="Google Analytics 4"
              type="Analytics"
              purpose="Identifies and distinguishes users for analytics. Stores and updates a unique value for each page visited."
              duration="24 hours"
              canOptOut={true}
            />

            {/* Functional */}
            <h6 className="fw-bold mb-3 mt-4" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
              Functional — Affiliate tracking
            </h6>
            <p className="text-muted mb-3" style={{ fontSize: '14px', lineHeight: 1.7 }}>
              This cookie is set only if you arrive at Dreamy Bedtime via an affiliate link. It contains no personal information about you — only the affiliate's code — and is used solely to attribute any resulting subscription to the correct affiliate partner.
            </p>

            <CookieRow
              name="dba (affiliate code)"
              provider="Dreamy Bedtime"
              type="Functional"
              purpose="Stores the affiliate referral code from an affiliate link click. Used to credit the referring affiliate partner with commission if you subscribe within the attribution window. Contains only the affiliate code — no personal data about you."
              duration="30 days"
              canOptOut={false}
            />
          </Section>

          <Section title="3. What We Do NOT Use">
            <ul className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <li className="mb-2"><strong>No advertising cookies:</strong> We do not run advertising campaigns. There are no tracking pixels, ad network cookies, retargeting cookies, or third-party advertising technologies on our site.</li>
              <li className="mb-2"><strong>No social media tracking pixels:</strong> We do not have Facebook Pixel, TikTok Pixel, Twitter/X pixel, or any similar tools.</li>
              <li className="mb-2"><strong>No cross-site tracking:</strong> We do not use any technology designed to follow you across other websites.</li>
              <li className="mb-2"><strong>No fingerprinting:</strong> We do not use browser fingerprinting or device fingerprinting techniques.</li>
            </ul>
          </Section>

          <Section title="4. Managing Your Cookie Preferences">
            <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <strong>Cookie banner:</strong> When you first visit Dreamy Bedtime, a cookie banner asks for your consent to analytics cookies. You can accept or decline. If you decline, only strictly necessary storage is used. Your choice is saved and the banner will not reappear.
            </p>
            <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <strong>Changing your decision:</strong> To withdraw consent or change your cookie preference, clear your browser's local storage for dreamybedtime.com. The cookie banner will reappear on your next visit. In Chrome: Settings → Privacy and Security → Site Settings → dreamybedtime.com → Clear data. Other browsers have similar options.
            </p>
            <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <strong>Opting out of Google Analytics:</strong> You can opt out of Google Analytics tracking across all websites using the official{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Google Analytics Opt-out Browser Add-on</a>.
            </p>
            <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <strong>Browser settings:</strong> You can also configure your browser to block or delete cookies globally. Note that blocking strictly necessary storage (local storage) will prevent you from staying logged in. Common browser cookie settings:
            </p>
            <ul className="text-muted mt-2" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <li className="mb-1"><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Chrome</a></li>
              <li className="mb-1"><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Firefox</a></li>
              <li className="mb-1"><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Safari</a></li>
              <li className="mb-1"><a href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Microsoft Edge</a></li>
            </ul>
          </Section>

          <Section title="5. Third-Party Services">
            <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              The following third-party services may set their own cookies or storage as part of their operation on our site:
            </p>
            <ul className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              <li className="mb-2"><strong>Google Analytics 4</strong> — analytics cookies described above. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Google's Privacy Policy</a>.</li>
              <li className="mb-2"><strong>Stripe</strong> — when you visit our pricing or checkout pages, Stripe's scripts may set cookies for fraud prevention and payment session management. These are set and controlled by Stripe. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Stripe's Privacy Policy</a>.</li>
            </ul>
            <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              We do not control the cookies set by third-party services. We select partners carefully and only work with services that have appropriate data processing agreements in place.
            </p>
          </Section>

          <Section title="6. Updates to This Policy">
            <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              We may update this Cookie Policy from time to time — for example, if we add new features or change our analytics provider. Material changes will be communicated via the cookie banner or email. The "Last updated" date at the top of this page will always reflect the most recent revision.
            </p>
          </Section>

          <Section title="7. Contact Us">
            <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
              For any questions about our use of cookies, contact us at{' '}
              <a href="mailto:support@dreamybedtime.com" style={{ color: '#6f42c1', fontWeight: 600 }}>support@dreamybedtime.com</a>{' '}
              or use our <Link to="/contact" style={{ color: '#6f42c1', fontWeight: 600 }}>Contact page</Link>.
            </p>
          </Section>

        </div>

        <div className="text-center mt-4">
          <p className="text-muted small">
            Also see our{' '}
            <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy Policy</Link>
            {' '}·{' '}
            <Link to="/terms" style={{ color: '#6f42c1' }}>Terms of Service</Link>
            {' '}·{' '}
            <Link to="/contact" style={{ color: '#6f42c1' }}>Contact Us</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
